import type { RouterOutputs } from "@repo/api";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import ShiftDialog from "~/components/shift/shift-dialog";
import { ShiftContext } from "~/hooks/use-shift";
import { api } from "~/lib/api";
import { useLoadingStore } from "~/stores/use-loading-store";
import { useShiftStore } from "~/stores/use-shift-store";
import OrderShiftDialog from "../components/shift/order-shift/order-shift-dialog";

export type Shift = RouterOutputs["shift"]["getAll"][number];
export type OrderShift = RouterOutputs["shift"]["getOrderShifts"][number];

interface ShiftProviderProps {
  children: ReactNode;
}

export const ShiftProvider = ({ children }: ShiftProviderProps) => {
  const { data: shifts, isFetching: isShiftsLoading } =
    api.shift.getAll.useQuery(undefined, {
      initialData: [],
    });

  const { data: orderShifts, isFetching: isOrderShiftsLoading } =
    api.shift.getOrderShifts.useQuery(undefined, {
      initialData: [],
    });

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  const {
    setShiftFormOpen,
    setCurrentShift,
    setCurrentOrderShift,
    setOrderShiftFormOpen,
  } = useShiftStore(
    useShallow((state) => ({
      setShiftFormOpen: state.setShiftFormOpen,
      setCurrentShift: state.setCurrentShift,
      setOrderShiftFormOpen: state.setOrderShiftFormOpen,
      setCurrentOrderShift: state.setCurrentOrderShift,
    })),
  );

  const handleShiftDialog = (shift?: Shift) => {
    setCurrentShift(shift);
    setShiftFormOpen(true);
  };

  const handleOrderShiftDialog = (shift?: OrderShift) => {
    setCurrentOrderShift(shift);
    setOrderShiftFormOpen(true);
  };

  useEffect(() => {
    if (isShiftsLoading) {
      setLoadingStage("shifts");
    } else {
      setLoadingStage(undefined);
    }
  }, [isShiftsLoading, setLoadingStage, shifts]);

  const context = {
    shifts,
    orderShifts,
    openShiftDialog: handleShiftDialog,
    openOrderShiftDialog: handleOrderShiftDialog,
  };

  return (
    <ShiftContext.Provider value={context}>
      {children}
      <ShiftDialog />
      <OrderShiftDialog />
    </ShiftContext.Provider>
  );
};
